import Card from '@/components/Card'
import { LIGHT_PRODUCT_CODE, PROFESSIONEL_PRODUCT_CODE, useMutationStripePortalUrl } from '@/hooks/useStripe'
import { useUrgentSystemNews } from '@/hooks/useSystemNews'
import { startTrialTaxInsight } from '@/services/taxInsights'
import { errorMessage } from '@/utils/toast'
import { Button } from '@headlessui/react'
import { ChartBarIcon, CheckBadgeIcon, CircleStackIcon, CurrencyDollarIcon, InformationCircleIcon, LockClosedIcon } from '@heroicons/react/24/outline'
import { CatchBoundary, createFileRoute, Outlet, useLoaderData, useRouter } from '@tanstack/react-router'
import clsx from 'clsx'
import { useState } from 'react'

export const Route = createFileRoute('/_protected/_insights')({
    component: InsightsLayout,
})

function InsightsLayout() {
    const { hasUrgentSystemNews } = useUrgentSystemNews()
    return (
        <>
            <div className="lg:hidden bg-white shadow h-16 fixed top-0 w-full z-30"></div>
            <main className={clsx("lg:pt-20 pt-16 w-full flex-1 flex flex-col", hasUrgentSystemNews && "lg:pt-[104px]")}>
                <div className="rounded-lg flex-1 flex flex-col">
                    <CatchBoundary
                        getResetKey={() => 'reset'}
                        onCatch={(error) => console.error(error)}
                    >
                        <RenderInner />
                    </CatchBoundary>
                </div>
            </main>

        </>
    )
}
function RenderInner() {
    const { user } = useLoaderData({ from: "/_protected" })

    if (!user.subscription?.details?.package?.level || user.subscription.details?.package?.level <= 1) {
        return (
            <NeedSubscription />
        )
    }

    if (!user.products.taxInsight) {
        return (
            <NoAccess />
        )
    }

    return <Outlet />
}
function NeedSubscription() {
    const { mutate, isPending } = useMutationStripePortalUrl("swap_" + LIGHT_PRODUCT_CODE)
    return (
        <Card className='max-w-xl mt-6 mx-auto text-center'>
            <div className='uppercase text-xs tracking-wide text-orange-500 flex justify-center'>
                <div className='bg-orange-500/10 px-2 py-0.5 rounded-sm flex items-center gap-1'>
                    <LockClosedIcon className='size-3' /> <span>Kræver aktivt abonnement</span>
                </div>
            </div>
            <h1 className='text-2xl text-dark-blue mt-4 mb-2'>
                Ingen adgang til <b>DMF Insight</b>
            </h1>
            <p className='text-sm'>Opgrader til <b>Light</b> for gratis prøveadgang i 14 dage, eller opgrader til <b>Professionel</b> for fuld adgang til DMF Insight</p>

            <div className='text-center mt-6'>
                <Button disabled={isPending} onClick={() => mutate()} className='btn btn-primary btn-lg'>Opgrader til Light</Button>
            </div>
        </Card>
    )
}

function NoAccess() {
    const { user } = useLoaderData({ from: "/_protected" })
    const { mutate, isPending } = useMutationStripePortalUrl("swap_" + PROFESSIONEL_PRODUCT_CODE)
    const { invalidate } = useRouter()
    const [loadingStartTrial, setLoadingStartTrial] = useState(false)

    async function startTrial() {
        try {
            setLoadingStartTrial(true)
            await startTrialTaxInsight()
            invalidate()
        } catch (error) {
            errorMessage(error)
        } finally {
            setLoadingStartTrial(false)
        }
    }

    return (
        <Card className='max-w-3xl m-auto space-y-4'>
            <div className='uppercase text-xs tracking-wide text-primary flex justify-center'>
                <div className='bg-primary/10 px-2 py-0.5 rounded-sm flex items-center gap-1'>
                    <LockClosedIcon className='size-3' /> <span>Kræver Professionel</span>
                </div>
            </div>

            <div className='text-center'>
                <h2 className='text-xl text-dark-blue font-semibold mb-2'>Få adgang til DMF Insights</h2>
                <p className='text-sm'>DMF Insights giver dig adgang til historiske værdifastsættelser udført af Dansk Motor Finans' afgiftspecialister, så du hurtigt kan få en stærk indikation af registreringsafgiften på tilsvarende køretøjer.</p>
            </div>

            <div className='flex gap-4'>
                <div className='p-3 border rounded-sm'>
                    <CheckBadgeIcon className='size-5 text-primary' />
                    <h3 className='text-sm text-dark-blue mt-2 mb-1'>Vurderet af mennesker – ikke kun beregnet af en robot</h3>
                    <p className='text-xs'>DMF Insights bygger på reelle værdifastsættelser udført af DMF's afgiftspecialister og ikke alene på automatiske beregningsmodeller.</p>
                </div>
                <div className='p-3 border rounded-sm'>
                    <CircleStackIcon className='size-5 text-primary' />
                    <h3 className='text-sm text-dark-blue mt-2 mb-1'>Et unikt datagrundlag fra virkelige sager</h3>
                    <p className='text-xs'>Se historiske vurderinger, handelspriser, nypriser og registreringsafgifter fra køretøjer, som Dansk Motor Finans faktisk har vurderet.</p>
                </div>
                <div className='p-3 border rounded-sm'>
                    <CurrencyDollarIcon className='size-5 text-primary' />
                    <h3 className='text-sm text-dark-blue mt-2 mb-1'>Køb den rigtige bil – til den rigtige pris</h3>
                    <p className='text-xs'>Sammenlign årgange og kilometerstand og se, hvilke biler der typisk giver det mest attraktive forhold mellem handelspris, nypris og registreringsafgift.</p>
                </div>
            </div>

            <div className='p-3 border bg-slate-50 rounded-sm flex gap-2'>
                <ChartBarIcon className='size-5 text-primary' />
                <div>
                    <h3 className='text-sm text-dark-blue mb-1'>Sæt en skarpere salgspris</h3>
                    <p className='text-xs'>Brug tidligere vurderinger som pejlemærke, når du vurderer indkøb, avance og forventet salgspris.</p>
                </div>
            </div>

            <div className='flex gap-4 justify-center'>
                {!user.details.taxInsightTrialEndsAt && (
                    <Button disabled={loadingStartTrial} onClick={startTrial} className={"btn btn-gray btn-lg"}>Prøv DMF Insight gratis i 14 dage</Button>
                )}
                <Button disabled={isPending} onClick={() => mutate()} className="btn btn-primary btn-lg">Opgrader til professionel</Button>
            </div>
            <hr className='border-t' />
            <div className='flex gap-2'>
                <InformationCircleIcon className='size-5 flex-shrink-0' />
                <div>
                    <h3 className='text-base text-dark-blue mb-1'>Har du brug for et konkret tal?</h3>
                    <p className='text-sm'>DMF Insights giver en markedsbaseret indikation baseret på historiske vurderinger. Med TAX får du en konkret forhåndsvurdering på det enkelte køretøj, som er bindende i 30 dage.</p>
                </div>
            </div>
        </Card>
    )
}
